import bilAPI from "../services/api";

// The file name a response gives in its Content-Disposition: `filename*=UTF-8''…` (RFC 5987) first, then
// `filename="…"`. Null when there is none (or the header is not exposed to the page, as cross-origin).
export function dispositionFilename(header) {
  if (!header) return null;
  const extended = /filename\*\s*=\s*([^;]+)/i.exec(header);
  if (extended) {
    // charset'language'percent-encoded name
    const value = extended[1].trim().replace(/^"(.*)"$/, "$1").replace(/^[^']*'[^']*'/, "");
    try {
      if (value) return decodeURIComponent(value);
    } catch (e) {
      // A malformed encoding: the plain filename below, if any.
    }
  }
  const plain = /filename\s*=\s*("((?:\\.|[^"\\])*)"|[^;]+)/i.exec(header);
  if (!plain) return null;
  const name = (plain[2] !== undefined ? plain[2].replace(/\\(.)/g, "$1") : plain[1]).trim();
  return name || null;
}

// Saves a Blob as `filename` through an object URL, revoked once the browser has taken the download.
export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not at once: some browsers start the download only after the click's task has ended.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// A failed blob request's body is a Blob too: its JSON ({detail, code}) goes into err.response.data, so that
// getCodedErrorMessage and getApiErrorCode read it as from any other request.
async function withParsedBody(err) {
  const data = err?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await data.text());
    } catch (e) {
      // Not JSON (a proxy's HTML page): no code, the caller's own message.
      err.response.data = null;
    }
  }
  return err;
}

/**
 * GETs a file from the API and saves it under the name its Content-Disposition gives, else `fallbackName`.
 * Resolves to {filename, size}; rejects with the axios error, its JSON body parsed (see withParsedBody).
 */
export async function downloadFile(url, { fallbackName = "download", params, signal } = {}) {
  let res;
  try {
    res = await bilAPI.get(url, { responseType: "blob", params, signal });
  } catch (err) {
    throw await withParsedBody(err);
  }
  const filename = dispositionFilename(res.headers?.["content-disposition"]) || fallbackName;
  saveBlob(res.data, filename);
  return { filename, size: res.data?.size ?? 0 };
}
